import React, { useEffect, useMemo, useRef, useState } from "react";
import "./style.css";
import DashboardPage from "./components/Dashboard";
import NewsletterPage from "./pages/NewsletterPage";
import ListsPage from "./pages/ListsPage";
import SMTPPage from "./pages/SMTPPage";
import { readCache, writeCache } from "./dataCache";
import AuthScreen from "./components/AuthScreen";
import AdminPanel from "./components/AdminPanel";
import AdminLogs from "./components/AdminLogs";
import SettingsDomains from "./components/SettingsDomains";
import ProfilePage from "./components/ProfilePage";
import SuppressionPage from "./components/SuppressionPage";
import MobileScrollArea from "./components/MobileScrollArea";
import { isLoggedIn, logout, getStoredUser, getViewAs, clearViewAs, setSession } from "./auth";
import DripsAdvanced from "./components/DripsAdvanced";
import ABSplitTest from "./components/ABSplitTest";

const API_URL = window.location.origin;


function Icon({ name, size = 19 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true"
  };

  if (name === "sun") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }

  if (name === "moon") {
    return (
      <svg {...common}>
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    );
  }

  if (name === "search") {
    return (
      <svg {...common}>
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    );
  }

  if (name === "settings") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 .6 1.65 1.65 0 0 0-.4 1.08V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8.6 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1-.6 1.65 1.65 0 0 0-1.08-.4H2a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 8.6a1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 7.04 3.9l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-.6 1.65 1.65 0 0 0 .4-1.08V3a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15.4 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.2.33.4.66.6 1h.09a2 2 0 1 1 0 4H20a1.65 1.65 0 0 0-.6 1z" />
      </svg>
    );
  }

  if (name === "bell") {
    return (
      <svg {...common}>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    );
  }

  if (name === "logout") {
    return (
      <svg {...common}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="m16 17 5-5-5-5" />
        <path d="M21 12H9" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M20 21a8 8 0 0 0-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function Page({ title, children }) {
  return (
    <section className="page-card">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function ContactsPage() {
  const [contacts, setContacts] = useState(() => readCache("contacts", []));
  const [lists, setLists] = useState(() => readCache("lists", []));
  const [tags, setTags] = useState(() => readCache("tags", []));

  const [tab, setTab] = useState("All");
  const [search, setSearch] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState("Profile");

  const [perPage, setPerPage] = useState(10);
  const [pageNo, setPageNo] = useState(1);

  const [editing, setEditing] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactSuccess, setContactSuccess] = useState("");

  const [exportOpen, setExportOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    tags: false,
    customFields: false,
    lists: false
  });

  const [selectedIds, setSelectedIds] = useState([]);
  const [menuOpen, setMenuOpen] = useState(null);

  const emptyForm = {
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    list: "",
    tag: ""
  };

  const [form, setForm] = useState(emptyForm);

  const EMAIL_DOMAIN_TYPOS_FRONTEND = {
    "gamil.com": "gmail.com",
    "gmial.com": "gmail.com",
    "gmai.com": "gmail.com",
    "gmal.com": "gmail.com",
    "gmail.co": "gmail.com",
    "gmail.con": "gmail.com",
    "gmail.cm": "gmail.com",
    "gmail.om": "gmail.com",
    "gmail.comn": "gmail.com",
    "gmail.cmo": "gmail.com",
    "gmaill.com": "gmail.com"
  };

  function getContactId(contact) {
    return String(contact?._id || contact?.id || "");
  }

  function getContactName(contact) {
    return (
      contact?.name ||
      `${contact?.firstName || ""} ${contact?.lastName || ""}`.trim() ||
      "-"
    );
  }

  function formatDate(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  }

  function validateEmailInDrawer(email = "") {
    const value = String(email || "").trim().toLowerCase();

    if (!value) return "Email address is required.";

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      return "Please enter a valid email address, for example name@gmail.com.";
    }

    const domain = value.split("@")[1] || "";

    if (EMAIL_DOMAIN_TYPOS_FRONTEND[domain]) {
      return `Invalid email domain "${domain}". Did you mean "${EMAIL_DOMAIN_TYPOS_FRONTEND[domain]}"?`;
    }

    if (
      domain.includes("gmail") &&
      domain !== "gmail.com" &&
      domain !== "googlemail.com"
    ) {
      return `Invalid Gmail domain "${domain}". Please use a real Gmail address like name@gmail.com.`;
    }

    return "";
  }

  function clearContactMessages() {
    setContactError("");
    setContactSuccess("");
  }

  function getProfessionalContactError(data, fallback = "Unable to save contact.") {
    const message = data?.message || fallback;

    if (data?.suggestion) return `${message} Suggested email: ${data.suggestion}`;
    if (String(message).toLowerCase().includes("valid email")) {
      return "Please enter a valid email address before saving this contact.";
    }
    if (String(message).toLowerCase().includes("cannot receive emails")) return message;
    if (String(message).toLowerCase().includes("temporary")) {
      return "Temporary or disposable email addresses are not allowed. Please use a real business or personal email address.";
    }
    if (String(message).toLowerCase().includes("already exists")) {
      return "Duplicate contacts are allowed. Please try saving again.";
    }

    return message;
  }

  async function loadData() {
    try {
      const contactsRes = await fetch(`${API_URL}/api/contacts`);
      const listsRes = await fetch(`${API_URL}/api/lists`);
      const tagsRes = await fetch(`${API_URL}/api/tags`);

      const contactsData = contactsRes.ok ? await contactsRes.json() : [];
      const listsData = listsRes.ok ? await listsRes.json() : [];
      const tagsData = tagsRes.ok ? await tagsRes.json() : [];

      const nextContacts = Array.isArray(contactsData) ? contactsData : [];
      const nextLists = Array.isArray(listsData) ? listsData : [];
      const nextTags = Array.isArray(tagsData) ? tagsData : [];
      setContacts(nextContacts);
      setLists(nextLists);
      setTags(nextTags);
      writeCache("contacts", nextContacts);
      writeCache("lists", nextLists);
      writeCache("tags", nextTags);
    } catch (err) {
      setContactError("Unable to load contacts. Please refresh the page.");
      console.log("Load error:", err.message);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  function openCreateDrawer() {
    setEditing(false);
    setSelected(null);
    setForm(emptyForm);
    clearContactMessages();
    setDrawerOpen(true);
  }

  function openEditDrawer(contact) {
    setEditing(true);
    setSelected(contact);
    setForm({
      firstName: contact.firstName || "",
      lastName: contact.lastName || "",
      email: contact.email || "",
      company: contact.company || "",
      list: contact.list || "",
      tag: contact.tag || ""
    });
    clearContactMessages();
    setMenuOpen(null);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    if (savingContact) return;
    setDrawerOpen(false);
    setForm(emptyForm);
    setEditing(false);
    clearContactMessages();
  }

  async function saveContact() {
    const email = form.email.trim().toLowerCase();
    const frontendError = validateEmailInDrawer(email);

    if (frontendError) {
      setContactError(frontendError);
      return;
    }

    try {
      setSavingContact(true);
      setContactError("");
      setContactSuccess("");

      const url =
        editing && selected
          ? `${API_URL}/api/contacts/${selected._id}`
          : `${API_URL}/api/contacts`;

      const method = editing && selected ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, email })
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok) {
        setContactError(
          getProfessionalContactError(
            data,
            "Unable to save contact. Please check the email address."
          )
        );
        return;
      }

      setContactSuccess(editing ? "Contact updated successfully." : "Contact created successfully.");
      setDrawerOpen(false);
      setForm(emptyForm);
      setEditing(false);

      await loadData();

      if (editing) setSelected(data);
      setTimeout(() => setContactSuccess(""), 2500);
    } catch (err) {
      setContactError("Something went wrong while saving this contact. Please try again.");
    } finally {
      setSavingContact(false);
    }
  }

  async function addList() {
    const name = prompt("New list name");
    if (!name) return;

    await fetch(`${API_URL}/api/lists`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name })
    });

    await loadData();
    setForm((prev) => ({ ...prev, list: name }));
  }

  async function addTag() {
    const name = prompt("New tag name");
    if (!name) return;

    await fetch(`${API_URL}/api/tags`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name })
    });

    await loadData();
    setForm((prev) => ({ ...prev, tag: name }));
  }

  async function unsubscribeContact(contact) {
    await fetch(`${API_URL}/api/contacts/${contact._id}/unsubscribe`, {
      method: "PATCH"
    });

    setSelected(null);
    await loadData();
    setTab("Unsubscribed");
  }

  async function deleteContact(contact) {
    if (!window.confirm("Delete this contact?")) return;

    await fetch(`${API_URL}/api/contacts/${contact._id}`, {
      method: "DELETE"
    });

    setMenuOpen(null);
    setSelected(null);
    setSelectedIds((prev) => prev.filter((id) => id !== getContactId(contact)));
    await loadData();
  }

  function toggleSelect(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleSelectAll() {
    const pageIds = pagedContacts.map((c) => getContactId(c)).filter(Boolean);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  }

  async function deleteSelectedContacts() {
    if (!selectedIds.length) return;
    if (!window.confirm("Delete selected contacts?")) return;

    for (const id of selectedIds) {
      await fetch(`${API_URL}/api/contacts/${id}`, { method: "DELETE" });
    }

    setSelectedIds([]);
    await loadData();
  }

  const counts = useMemo(
    () => ({
      all: contacts.length,
      active: contacts.filter(
        (c) => (c.status || "active") === "active" || c.status === "subscribed"
      ).length,
      unsubscribed: contacts.filter((c) => c.status === "unsubscribed").length,
      bounced: contacts.filter((c) => c.status === "bounced").length,
      spam: contacts.filter((c) => c.status === "spam").length
    }),
    [contacts]
  );

  const filteredContacts = useMemo(() => {
    const q = search.toLowerCase();

    return contacts.filter((c) => {
      const status = c.status || "active";
      const fullName = getContactName(c);

      const matchesSearch =
        fullName.toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.company || "").toLowerCase().includes(q);

      if (!matchesSearch) return false;
      if (tab === "Active") return status === "active" || status === "subscribed";
      if (tab === "Unsubscribed") return status === "unsubscribed";
      if (tab === "Bounced") return status === "bounced";
      if (tab === "Marked Spam") return status === "spam";
      return true;
    });
  }, [contacts, search, tab]);

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / perPage));

  const pagedContacts = filteredContacts.slice(
    (pageNo - 1) * perPage,
    pageNo * perPage
  );

  useEffect(() => {
    setPageNo(1);
    setSelectedIds([]);
    setMenuOpen(null);
  }, [tab, search, perPage]);

  function exportContactsCSV() {
    const headers = ["Name", "Email", "Company"];

    if (exportOptions.tags) headers.push("Tag");
    if (exportOptions.lists) headers.push("List");
    if (exportOptions.customFields) headers.push("Custom Fields");

    const rows = filteredContacts.map((c) => {
      const row = [getContactName(c), c.email || "", c.company || ""];
      if (exportOptions.tags) row.push(c.tag || "");
      if (exportOptions.lists) row.push(c.list || "");
      if (exportOptions.customFields) row.push("No custom fields");
      return row;
    });

    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(",")
      )
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "contacts-export.csv";
    link.click();

    URL.revokeObjectURL(url);
    setExportOpen(false);
  }

  if (selected && !drawerOpen) {
    return (
      <section className="contacts-page">
        {contactSuccess && <div className="contact-notice success">{contactSuccess}</div>}
        {contactError && !drawerOpen && <div className="contact-notice error">{contactError}</div>}

        <div className="contact-detail-top">
          <div className="breadcrumb-line">
            <button onClick={() => setSelected(null)}>‹ Contacts</button>
            <span>/</span>
            <strong>{getContactName(selected)}</strong>
          </div>

          <div className="contact-detail-actions">
            <button className="yellow-btn compact" onClick={() => openEditDrawer(selected)}>
              Edit Contact
            </button>

            <button className="danger-outline" onClick={() => unsubscribeContact(selected)}>
              Unsubscribe
            </button>

            <button className="danger-outline" onClick={() => deleteContact(selected)}>
              Delete
            </button>
          </div>
        </div>

        <div className="contact-tabs detail-tabs">
          {["Profile", "Timeline"].map((item) => (
            <button
              key={item}
              className={detailTab === item ? "active" : ""}
              onClick={() => setDetailTab(item)}
            >
              {item}
            </button>
          ))}
        </div>

        {detailTab === "Profile" ? (
          <div className="contact-profile-grid">
            <div>
              <h3>Personal information</h3>

              <div className="contact-profile-card">
                <div>
                  <span>First name</span>
                  <b>{selected.firstName || selected.name || "-"}</b>
                </div>

                <div>
                  <span>Last name</span>
                  <b>{selected.lastName || "-"}</b>
                </div>

                <div>
                  <span>Email</span>
                  <b>{selected.email}</b>
                </div>

                <div>
                  <span>Company</span>
                  <b>{selected.company || "-"}</b>
                </div>
              </div>

              <h3 className="section-gap">Custom Fields</h3>
              <p className="muted">No custom fields found for this contact.</p>
            </div>

            <div>
              <h3>Lists</h3>
              <p className="muted">{selected.list || "No lists found for this contact."}</p>

              <h3 className="section-gap">Tags</h3>
              <p className="muted">{selected.tag || "No tags found for this contact."}</p>
            </div>
          </div>
        ) : (
          <div className="timeline-area">
            <h3>{selected.createdAt ? new Date(selected.createdAt).toDateString() : new Date().toDateString()}</h3>

            <div className="timeline-card">
              <span className="timeline-icon">👤</span>
              <p>
                <b>{getContactName(selected)}</b> contact created manually
              </p>
            </div>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="contacts-page">
      {contactSuccess && <div className="contact-notice success">{contactSuccess}</div>}
      {contactError && !drawerOpen && <div className="contact-notice error">{contactError}</div>}

      <div className="contacts-header">
        <h2>Contacts</h2>

        <button className="yellow-btn" onClick={openCreateDrawer}>
          ＋ Create Contact
        </button>
      </div>

      <div className="contact-tabs">
        {["All", "Active", "Unsubscribed", "Bounced", "Marked Spam", "Analytics"].map(
          (item) => (
            <button
              key={item}
              className={tab === item ? "active" : ""}
              onClick={() => setTab(item)}
            >
              {item} <span>ⓘ</span>
            </button>
          )
        )}
      </div>

      {tab === "Analytics" ? (
        <ContactsAnalytics counts={counts} />
      ) : tab !== "All" && filteredContacts.length === 0 ? (
        <div className="empty-contact-state">
          <h3>
            {tab === "Active" && "No active contacts"}
            {tab === "Unsubscribed" && "No unsubscribed contacts"}
            {tab === "Bounced" && "No bounced contacts"}
            {tab === "Marked Spam" && "No contacts marked as spam"}
          </h3>
          <p>Once a contact is {tab.toLowerCase()}, they will appear here.</p>
        </div>
      ) : (
        <div className="contacts-table-card">
          {selectedIds.length > 0 ? (
            <div className="contacts-bulk-bar">
              <button className="delete-selected-btn" onClick={deleteSelectedContacts}>
                Delete Selected Contacts
              </button>

              <button className="contacts-filter-icon" type="button">☷</button>
            </div>
          ) : (
            <div className="contacts-table-toolbar">
              <p>All {filteredContacts.length} contacts</p>

              <div className="contacts-search-wrap">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search contacts..."
                />

                <button type="button">☷</button>
              </div>
            </div>
          )}

          <MobileScrollArea className="contacts-table-scroll contacts-ref-scroll" ariaLabel="contacts table">
            <table className="contacts-table contact-ref-table">
              <thead>
                <tr>
                  <th className="check-col">
                    <input
                      className="contact-checkbox"
                      type="checkbox"
                      checked={
                        pagedContacts.length > 0 &&
                        pagedContacts.every((c) => selectedIds.includes(getContactId(c)))
                      }
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Date Created⌄</th>
                  <th>Date Updated</th>
                  <th className="menu-col"></th>
                </tr>
              </thead>

              <tbody>
                {pagedContacts.map((contact) => {
                  const id = getContactId(contact);

                  return (
                    <tr key={id} onClick={() => setSelected(contact)}>
                      <td className="check-col" onClick={(e) => e.stopPropagation()}>
                        <input
                          className="contact-checkbox"
                          type="checkbox"
                          checked={selectedIds.includes(id)}
                          onChange={() => toggleSelect(id)}
                        />
                      </td>

                      <td className="contact-name-cell">{getContactName(contact)}</td>
                      <td>{contact.email}</td>
                      <td>{formatDate(contact.createdAt)}</td>
                      <td>{formatDate(contact.updatedAt || contact.createdAt)}</td>

                      <td className="contact-row-menu-cell" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="contact-dots-btn"
                          type="button"
                          onClick={() => setMenuOpen(menuOpen === id ? null : id)}
                        >
                          ⋮
                        </button>

                        {menuOpen === id && (
                          <div className="contact-dropdown">
                            <button type="button" onClick={() => setSelected(contact)}>Info</button>
                            <button type="button" onClick={() => openEditDrawer(contact)}>Edit</button>
                            <button type="button" className="delete" onClick={() => deleteContact(contact)}>
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {pagedContacts.length === 0 && (
                  <tr>
                    <td colSpan="6" className="smtp-empty">No contacts found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </MobileScrollArea>

          {selectedIds.length === 0 && (
            <div className="contacts-table-footer">
              <div>
                <button className="export-btn" onClick={() => setExportOpen(true)}>
                  ⇩ Export
                </button>

                <span>
                  Showing {pagedContacts.length ? (pageNo - 1) * perPage + 1 : 0} to{" "}
                  {Math.min(pageNo * perPage, filteredContacts.length)} of {filteredContacts.length} results
                </span>
              </div>

              <div>
                <span>Items per page</span>

                <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>

                <button disabled={pageNo <= 1} onClick={() => setPageNo(pageNo - 1)}>←</button>
                <button disabled={pageNo >= totalPages} onClick={() => setPageNo(pageNo + 1)}>→</button>
              </div>
            </div>
          )}
        </div>
      )}

      {drawerOpen && (
        <ContactDrawer
          title={editing ? "Edit Contact" : "Create Contact"}
          form={form}
          setForm={(next) => {
            setContactError("");
            setForm(next);
          }}
          lists={lists}
          tags={tags}
          onClose={closeDrawer}
          onSave={saveContact}
          onAddList={addList}
          onAddTag={addTag}
          saveText={editing ? "Save" : "Create"}
          error={contactError}
          saving={savingContact}
        />
      )}

      {exportOpen && (
        <ExportModal
          exportOptions={exportOptions}
          setExportOptions={setExportOptions}
          onClose={() => setExportOpen(false)}
          onExport={exportContactsCSV}
        />
      )}
    </section>
  );
}

function ExportModal({ exportOptions, setExportOptions, onClose, onExport }) {
  return (
    <div className="export-modal-overlay">
      <div className="export-modal">
        <h2>Export Contacts</h2>

        <p>
          By default, the export only includes basic contact fields. Add optional
          columns below if you need them.
        </p>

        <label>
          <input
            type="checkbox"
            checked={exportOptions.tags}
            onChange={(e) =>
              setExportOptions({ ...exportOptions, tags: e.target.checked })
            }
          />
          Include Tags column <span>ⓘ</span>
        </label>

        <label>
          <input
            type="checkbox"
            checked={exportOptions.customFields}
            onChange={(e) =>
              setExportOptions({
                ...exportOptions,
                customFields: e.target.checked
              })
            }
          />
          Include Custom Field columns <span>ⓘ</span>
        </label>

        <label>
          <input
            type="checkbox"
            checked={exportOptions.lists}
            onChange={(e) =>
              setExportOptions({ ...exportOptions, lists: e.target.checked })
            }
          />
          Include Lists column <span>ⓘ</span>
        </label>

        <div className="export-modal-actions">
          <button onClick={onClose}>Cancel</button>
          <button className="yellow-btn" onClick={onExport}>
            Export
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactDrawer({
  title,
  form,
  setForm,
  lists,
  tags,
  onClose,
  onSave,
  onAddList,
  onAddTag,
  saveText,
  error,
  saving
}) {
  return (
    <div className="drawer-overlay">
      <div className="drawer-blur" onClick={saving ? undefined : onClose}></div>

      <aside className="contact-drawer">
        <div className="drawer-head">
          <h2>{title}</h2>
          <button disabled={saving} onClick={onClose}>
            ×
          </button>
        </div>

        <label>First Name</label>
        <input
          value={form.firstName}
          disabled={saving}
          onChange={(e) => setForm({ ...form, firstName: e.target.value })}
        />

        <label>Last Name</label>
        <input
          value={form.lastName}
          disabled={saving}
          onChange={(e) => setForm({ ...form, lastName: e.target.value })}
        />

        <label>
          Email <b>*</b>
        </label>

        <input
          className={error ? "input-error" : ""}
          value={form.email}
          disabled={saving}
          placeholder="name@gmail.com"
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />

        {error && (
          <div className="drawer-email-error">
            <strong>Invalid email address</strong>
            <p>{error}</p>
          </div>
        )}

        <label>Company</label>
        <input
          value={form.company}
          disabled={saving}
          onChange={(e) => setForm({ ...form, company: e.target.value })}
        />

        <label>List</label>
        <div className="drawer-select-row">
          <select
            value={form.list}
            disabled={saving}
            onChange={(e) => setForm({ ...form, list: e.target.value })}
          >
            <option value="">Select from available lists</option>

            {lists.map((list) => (
              <option key={list._id || list.id || list.name} value={list.name}>
                {list.name}
              </option>
            ))}
          </select>

          <button disabled={saving} onClick={onAddList}>
            Add
          </button>
        </div>

        <label>Tag</label>
        <div className="drawer-select-row">
          <select
            value={form.tag}
            disabled={saving}
            onChange={(e) => setForm({ ...form, tag: e.target.value })}
          >
            <option value="">Select from available tags</option>

            {tags.map((tag) => (
              <option key={tag._id || tag.id || tag.name} value={tag.name}>
                {tag.name}
              </option>
            ))}
          </select>

          <button disabled={saving} onClick={onAddTag}>
            Add
          </button>
        </div>

        <div className="custom-fields-row">
          <h3>Custom Fields</h3>
          <button disabled={saving}>Add</button>
        </div>

        <div className="drawer-actions">
          <button disabled={saving} onClick={onClose}>
            Cancel
          </button>

          <button className="yellow-btn" disabled={saving} onClick={onSave}>
            {saving ? "Saving..." : saveText}
          </button>
        </div>
      </aside>
    </div>
  );
}

function ContactsAnalytics({ counts }) {
  // Local (user-timezone) YYYY-MM-DD so post-midnight times bucket into today.
  const fmtISO = (d) => {
    const x = new Date(d);
    return new Date(x.getTime() - x.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);
  };
  const initEnd = new Date();
  const initStart = new Date();

  const [start, setStart] = useState(fmtISO(initStart));
  const [end, setEnd] = useState(fmtISO(initEnd));
  const [activePreset, setActivePreset] = useState("Today");
  const [data, setData] = useState(() => readCache("analytics_" + fmtISO(initStart) + "_" + fmtISO(initEnd), null));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!readCache("analytics_" + start + "_" + end, null)) setLoading(true);
    fetch(`${API_URL}/api/analytics/audience?start=${start}&end=${end}`)
      .then((r) => r.json())
      .then((d) => { if (active) { setData(d); writeCache("analytics_" + start + "_" + end, d); } })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [start, end]);

  const PRESETS = ["Today", "Yesterday", "Last 7 days", "Last 30 days", "Last 3 months", "Last year"];

  function applyPreset(label) {
    const e = new Date();
    const s = new Date();
    if (label === "Today") { /* both today */ }
    else if (label === "Yesterday") { s.setDate(s.getDate() - 1); e.setDate(e.getDate() - 1); }
    else if (label === "Last 7 days") s.setDate(s.getDate() - 7);
    else if (label === "Last 30 days") s.setDate(s.getDate() - 30);
    else if (label === "Last 3 months") s.setMonth(s.getMonth() - 3);
    else if (label === "Last year") s.setFullYear(s.getFullYear() - 1);
    setStart(fmtISO(s));
    setEnd(fmtISO(e));
    setActivePreset(label);
  }

  const totals = data?.totals || {};
  // backend countBy() returns arrays of { label, value }
  const asRows = (v) => (Array.isArray(v) ? v : []);
  const sources = asRows(data?.sources);
  const countries = asRows(data?.countries);
  const devices = asRows(data?.devices);
  const clients = asRows(data?.clients);
  const newSeries = Array.isArray(data?.newContactsSeries) ? data.newContactsSeries : [];
  const engSeries = Array.isArray(data?.engagementSeries) ? data.engagementSeries : [];

  const safeMax = (nums) => nums.reduce((m, n) => (Number(n) > m ? Number(n) : m), 1);
  const maxNew = safeMax(newSeries.flatMap((p) => [p.added, p.unsubscribed]));
  const maxEng = safeMax(engSeries.flatMap((p) => [p.sent, p.opened, p.clicked]));

  const renderRows = (rows, emptyText) => {
    if (!rows.length) return <p className="analytics-empty-text">{emptyText}</p>;
    const max = safeMax(rows.map((r) => r.value));
    return [...rows]
      .sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))
      .map((r) => (
        <div className="source-line" key={r.label}>
          <span>{r.label || "Other"}</span>
          <i style={{ width: `${((Number(r.value) || 0) / max) * 100}%` }}></i>
          <b>{Number(r.value) || 0}</b>
        </div>
      ));
  };

  return (
    <div className="contacts-analytics">
      <div className="analytics-top">
        <h3>Audience Analytics</h3>

        <div className="analytics-date-controls">
          <div className="analytics-presets">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                className={activePreset === p ? "active" : ""}
                onClick={() => applyPreset(p)}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="analytics-date-range">
            <label>
              From
              <input
                type="date"
                value={start}
                max={end}
                onChange={(e) => { setStart(e.target.value); setActivePreset(""); }}
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={end}
                min={start}
                onChange={(e) => { setEnd(e.target.value); setActivePreset(""); }}
              />
            </label>
          </div>
        </div>
      </div>

      {loading && !data && <div className="analytics-loading">Loading analytics…</div>}

      <div className="stats-grid">
        <div className="stat-card">
          <p>New Contacts ⓘ</p>
          <h1>{totals.newContacts ?? 0}</h1>
          <span className="green-text">in selected range</span>
        </div>

        <div className="stat-card">
          <p>Unsubscribers ⓘ</p>
          <h1>{totals.unsubscribers ?? 0}</h1>
          <span>in selected range</span>
        </div>

        <div className="stat-card">
          <p>Open rate ⓘ</p>
          <h1>{totals.openRate ?? 0}%</h1>
          <span>{totals.opens ?? 0} opens</span>
        </div>

        <div className="stat-card">
          <p>Click rate ⓘ</p>
          <h1>{totals.clickRate ?? 0}%</h1>
          <span>{totals.clicks ?? 0} clicks</span>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="analytics-card large">
          <h3>New Contacts ⓘ</h3>
          <p>Daily new and unsubscribed contacts</p>
          {newSeries.length ? (
            <div className="mini-bar-chart">
              {newSeries.map((pt) => (
                <div
                  className="mini-bar-col"
                  key={pt.date}
                  title={`${pt.date}: +${pt.added} new / -${pt.unsubscribed} unsub`}
                >
                  <div className="mini-bar added" style={{ height: `${(pt.added / maxNew) * 100}%` }}></div>
                  <div className="mini-bar unsub" style={{ height: `${(pt.unsubscribed / maxNew) * 100}%` }}></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="analytics-empty">No new contacts in this range.</div>
          )}
        </div>

        <div className="analytics-card large">
          <h3>Engagement ⓘ</h3>
          <p>Opens, clicks and sent over time</p>
          {engSeries.length ? (
            <div className="mini-bar-chart">
              {engSeries.map((pt) => (
                <div
                  className="mini-bar-col"
                  key={pt.date}
                  title={`${pt.date}: ${pt.sent} sent, ${pt.opened} opened, ${pt.clicked} clicked`}
                >
                  <div className="mini-bar sent" style={{ height: `${(pt.sent / maxEng) * 100}%` }}></div>
                  <div className="mini-bar opened" style={{ height: `${(pt.opened / maxEng) * 100}%` }}></div>
                  <div className="mini-bar clicked" style={{ height: `${(pt.clicked / maxEng) * 100}%` }}></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="analytics-empty">No engagement data for the selected date range.</div>
          )}
        </div>

        <div className="analytics-card">
          <h3>Signup sources ⓘ</h3>
          <p>How new contacts originally joined</p>
          {renderRows(sources, "No signups in this range.")}
        </div>

        <div className="analytics-card">
          <h3>Top countries ⓘ</h3>
          <p>Unique contacts who opened or clicked, by country</p>
          {renderRows(countries, "No country data for this period.")}
        </div>

        <div className="analytics-card wide">
          <h3>Device & client breakdown</h3>

          <div className="breakdown-grid">
            <div>
              <h4>Device Breakdown</h4>
              {renderRows(devices, "No device breakdown data for the selected date range.")}
            </div>

            <div>
              <h4>Email Client Breakdown</h4>
              {renderRows(clients, "No email client data for the selected date range.")}
            </div>
          </div>
        </div>

        <div className="analytics-card wide">
          <h3>Unsubscribe Analytics</h3>
          {data?.unsubscribeCount ? (
            <p>{data.unsubscribeCount} unsubscribe events in this period.</p>
          ) : (
            <p>No unsubscribe data for this period.</p>
          )}
        </div>
      </div>
    </div>
  );
}


function formatNotificationTime(value) {
  if (!value) return "Just now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Just now";
  return date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function GlobalSearchPanel({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/global-search?q=${encodeURIComponent(q)}`);
        const data = res.ok ? await res.json() : { contacts: [] };
        setResults(Array.isArray(data.contacts) ? data.contacts : []);
      } catch (err) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query, open]);

  if (!open) return null;

  return (
    <div className="sx-search-overlay" onClick={onClose}>
      <div className="sx-global-search-modal" onClick={(event) => event.stopPropagation()}>
        <div className="sx-global-search-input-row">
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search contacts by name, email, list or campaign..."
          />
          <button type="button" onClick={onClose}>×</button>
        </div>

        <div className="sx-global-search-body">
          {!query.trim() && (
            <div className="sx-search-empty">
              <Icon name="contact" size={30} />
              <h3>Search saved contacts</h3>
              <p>Type a name, email, list, tag or campaign to find complete contact info.</p>
            </div>
          )}

          {query.trim() && loading && <p className="sx-search-loading">Searching...</p>}

          {query.trim() && !loading && results.length === 0 && (
            <div className="sx-search-empty">
              <Icon name="contact" size={30} />
              <h3>No contacts found</h3>
              <p>No saved contact matched your search.</p>
            </div>
          )}

          {results.map((item) => (
            <div className="sx-search-result" key={item.id || item.email}>
              <div className="sx-result-avatar">{String(item.name || item.email || "?").charAt(0).toUpperCase()}</div>
              <div>
                <h4>{item.name || "Unnamed contact"}</h4>
                <p>{item.email}</p>
                <div className="sx-result-meta">
                  <span>Contact: {item.status || "saved"}</span>
                  {item.company && <span>Company: {item.company}</span>}
                  {(item.lists || []).length > 0 && <span>Lists: {item.lists.join(", ")}</span>}
                  {item.list && !(item.lists || []).includes(item.list) && <span>List: {item.list}</span>}
                  {(item.tags || []).length > 0 && <span>Tags: {item.tags.join(", ")}</span>}
                  {item.tag && <span>Tag: {item.tag}</span>}
                  {(item.campaigns || []).length > 0 && <span>Campaigns: {item.campaigns.join(", ")}</span>}
                  {(item.foundIn || []).length > 0 && <span>Found in: {item.foundIn.join(", ")}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NotificationsDropdown({ open }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    async function loadNotifications() {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/api/notifications`);
        const data = res.ok ? await res.json() : { notifications: [] };
        if (mounted) setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
      } catch (err) {
        if (mounted) setNotifications([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadNotifications();
    return () => { mounted = false; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="sx-notifications-menu">
      <div className="sx-notifications-head">
        <h3>Notifications</h3>
        <span>{notifications.length}</span>
      </div>
      {loading && <p className="sx-notification-empty">Loading...</p>}
      {!loading && notifications.length === 0 && <p className="sx-notification-empty">No notifications yet.</p>}
      {!loading && notifications.map((item) => (
        <div className="sx-notification-item" key={item.id}>
          <b>{item.title}</b>
          <p>{item.description}</p>
          <small>{formatNotificationTime(item.createdAt)}</small>
        </div>
      ))}
    </div>
  );
}

function pageFromHash() {
  const raw = (window.location.hash || "#Dashboard").replace(/^#\/?/, "");
  const first = decodeURIComponent(raw.split("/")[0] || "Dashboard");
  const allowed = [
    "Dashboard", "Newsletters", "Drips", "AbSplitTest", "Auto-Trigger", "RSS",
    "Contacts", "Lists", "Tags", "Segments", "SMTPs", "Popup", "Embedded Form",
    "Landing Page", "Surveys", "Polls", "Automations", "Workflows", "Bulk Actions",
    "Queue", "Unsubscribe", "Users", "Logs", "Domain", "Profile"
  ];
  return allowed.includes(first) ? first : "Dashboard";
}

const MENU_GROUPS = {
  Campaigns: ["Newsletters", "Drips", "AbSplitTest", "Auto-Trigger", "RSS"],
  Audience: ["Contacts", "Lists", "Tags", "Segments"],
  Forms: ["Popup", "Embedded Form", "Landing Page", "Surveys", "Polls"],
  Automate: ["Automations", "Workflows", "Bulk Actions", "Queue", "Unsubscribe"],
};

export default function App() {
  const [page, setPageState] = useState(pageFromHash());
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("sx-theme") !== "light");
  const [authed, setAuthed] = useState(() => isLoggedIn());
  const [userVersion, setUserVersion] = useState(0);
  const currentUser = useMemo(() => getStoredUser(), [userVersion, authed]);
  const isAdmin = String(currentUser?.role || "").toLowerCase() === "admin";
  // When an admin has chosen "View data" for a user, we show a banner and every
  // API call is scoped to that user (see the fetch interceptor in auth.js).
  const viewAs = isAdmin ? getViewAs() : null;
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  function toggleMenu(menu) {
    setOpenMenu((current) => (current === menu ? null : menu));
  }

  function setPage(nextPage) {
    const nextHash = `#/${encodeURIComponent(nextPage)}`;
    if (window.location.hash !== nextHash) {
      window.location.hash = nextHash;
    }
    setPageState(nextPage);
    setMobileMenuOpen(false);
    // Accordion: opening any page closes the previously open menu; if the page
    // belongs to a group, keep only that group open (top-level pages close all).
    const owner = Object.keys(MENU_GROUPS).find((m) => MENU_GROUPS[m].includes(nextPage)) || null;
    setOpenMenu(owner);
  }

  useEffect(() => {
    const onHashChange = () => setPageState(pageFromHash());
    window.addEventListener("hashchange", onHashChange);
    onHashChange();
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    localStorage.setItem("sx-theme", darkMode ? "dark" : "light");
    document.body.classList.toggle("sx-light-theme", !darkMode);
  }, [darkMode]);

  // Gate the whole app behind login. Also shows the auth screen for the
  // public reset-password / verify-email hash links even when logged out.
  const authHash = (page || "").toLowerCase();
  const forceAuthView =
    (window.location.hash || "").toLowerCase().match(/reset-password|verify-email|forgot-password/);
  if (!authed || forceAuthView) {
    return (
      <AuthScreen
        darkMode={darkMode}
        onAuthenticated={() => {
          window.location.hash = "";
          setAuthed(true);
        }}
      />
    );
  }

  const pages = {
    Dashboard: <DashboardPage onOpenNewsletterPerformance={(id, sub) => { const base = `#/Newsletters/campaign/${id}/performance`; window.location.hash = sub ? `${base}/${sub}` : base; setPageState("Newsletters"); }} onCreateNewsletter={() => { window.location.hash = "#/Newsletters/new"; setPageState("Newsletters"); }} />,
    Newsletters: <NewsletterPage />,
    Drips: (
    <DripsAdvanced />
  ),
    "AbSplitTest": (
      <Page title="AbSplitTest"><ABSplitTest /></Page>
    ),
    "Auto-Trigger": (
      <Page title="Auto-Trigger">
        <p>Trigger emails on signup, tag added, or campaign action.</p>
      </Page>
    ),
    RSS: (
      <Page title="RSS Campaigns">
        <p>Create newsletters from RSS feed updates.</p>
      </Page>
    ),
    Contacts: <ContactsPage />,
    Lists: <ListsPage />,
    SMTPs: <SMTPPage />,
    Profile: (
      <ProfilePage
        currentUser={currentUser}
        onUpdated={(u) => {
          setSession("", u);
          setUserVersion((v) => v + 1);
        }}
      />
    ),
    Tags: (
      <Page title="Tags">
        <p>Lead, Customer, Trial, VIP</p>
      </Page>
    ),
    Segments: (
      <Page title="Segments">
        <p>Active Contacts, Opened Last Campaign, Clicked Link</p>
      </Page>
    ),
    Popup: (
      <Page title="Popup Forms">
        <button className="yellow-btn">Create Popup</button>
      </Page>
    ),
    "Embedded Form": (
      <Page title="Embedded Forms">
        <p>Generate embed code for website forms.</p>
      </Page>
    ),
    "Landing Page": (
      <Page title="Landing Pages">
        <button className="yellow-btn">Create Landing Page</button>
      </Page>
    ),
    Surveys: (
      <Page title="Surveys">
        <button className="yellow-btn">Create Survey</button>
      </Page>
    ),
    Polls: (
      <Page title="Polls">
        <button className="yellow-btn">Create Poll</button>
      </Page>
    ),
    Automations: (
      <Page title="Automations">
        <button className="yellow-btn">Create Automation</button>
      </Page>
    ),
    Workflows: (
      <Page title="Workflows">
        <p>Visual workflow builder placeholder.</p>
      </Page>
    ),
    "Bulk Actions": (
      <Page title="Bulk Actions">
        <p>Bulk tag, unsubscribe, move to list.</p>
      </Page>
    ),
    Queue: (
      <Page title="Sending Queue">
        <p>Queued, sent, failed, paused email jobs.</p>
      </Page>
    ),
    Unsubscribe: <SuppressionPage />
  };

  // Admin-only pages. Hidden from the nav for normal users and gated server-side.
  if (isAdmin) {
    pages.Users = <AdminPanel />;
    pages.Logs = <AdminLogs />;
    pages.Domain = <SettingsDomains />;
  }

  return (
    <div className={darkMode ? "sx-app sx-dark" : "sx-app sx-light"}>
      <nav className={mobileMenuOpen ? "sx-navbar sx-mobile-menu-open" : "sx-navbar"} aria-label="Main navigation">
        <div className="brand">✈</div>
        <div className="project">Test-1</div>

        <button className={page === "Dashboard" ? "active" : ""} onClick={() => setPage("Dashboard")}>Dashboard</button>

        <div className={["nav-menu", openMenu === "Campaigns" ? "open" : "", ["Newsletters", "Drips", "AbSplitTest", "Auto-Trigger", "RSS"].includes(page) ? "has-active" : ""].filter(Boolean).join(" ")}>
          <div className="nav-menu-label" onClick={() => toggleMenu("Campaigns")}>Campaigns<span className="nav-caret">⌄</span></div>
          <div className="dropdown">
            {["Newsletters", "Drips", "AbSplitTest", "Auto-Trigger", "RSS"].map(
              (x) => (
                <p className={page === x ? "active" : ""} onClick={(e) => { e.stopPropagation(); setPage(x); }} key={x}>
                  {x}
                </p>
              )
            )}
          </div>
        </div>

        <div className={["nav-menu", openMenu === "Audience" ? "open" : "", ["Contacts", "Lists", "Tags", "Segments"].includes(page) ? "has-active" : ""].filter(Boolean).join(" ")}>
          <div className="nav-menu-label" onClick={() => toggleMenu("Audience")}>Audience<span className="nav-caret">⌄</span></div>
          <div className="dropdown">
            {["Contacts", "Lists", "Tags", "Segments"].map((x) => (
              <p className={page === x ? "active" : ""} onClick={(e) => { e.stopPropagation(); setPage(x); }} key={x}>
                {x}
              </p>
            ))}
          </div>
        </div>

        <div className={["nav-menu", openMenu === "Forms" ? "open" : "", ["Popup", "Embedded Form", "Landing Page", "Surveys", "Polls"].includes(page) ? "has-active" : ""].filter(Boolean).join(" ")}>
          <div className="nav-menu-label" onClick={() => toggleMenu("Forms")}>Forms<span className="nav-caret">⌄</span></div>
          <div className="dropdown">
            {["Popup", "Embedded Form", "Landing Page", "Surveys", "Polls"].map(
              (x) => (
                <p className={page === x ? "active" : ""} onClick={(e) => { e.stopPropagation(); setPage(x); }} key={x}>
                  {x}
                </p>
              )
            )}
          </div>
        </div>

        <div className={["nav-menu", openMenu === "Automate" ? "open" : "", ["Automations", "Workflows", "Bulk Actions", "Queue", "Unsubscribe"].includes(page) ? "has-active" : ""].filter(Boolean).join(" ")}>
          <div className="nav-menu-label" onClick={() => toggleMenu("Automate")}>Automate<span className="nav-caret">⌄</span></div>
          <div className="dropdown">
            {["Automations", "Workflows", "Bulk Actions", "Queue", "Unsubscribe"].map(
              (x) => (
                <p className={page === x ? "active" : ""} onClick={(e) => { e.stopPropagation(); setPage(x); }} key={x}>
                  {x}
                </p>
              )
            )}
          </div>
        </div>

        {isAdmin ? (
          <>
            <button className={page === "Users" ? "active sx-nav-admin sx-nav-users" : "sx-nav-admin sx-nav-users"} onClick={() => setPage("Users")}>Users<span className="nav-caret">⌄</span></button>
            <button className={page === "Logs" ? "active sx-nav-admin sx-nav-logs" : "sx-nav-admin sx-nav-logs"} onClick={() => setPage("Logs")}>Logs<span className="nav-caret">⌄</span></button>
            <button className={page === "SMTPs" ? "active" : ""} onClick={() => setPage("SMTPs")}>SMTPs<span className="nav-caret">⌄</span></button>
            <button className={page === "Domain" ? "active sx-nav-admin sx-nav-domain" : "sx-nav-admin sx-nav-domain"} onClick={() => setPage("Domain")}>Domain<span className="nav-caret">⌄</span></button>
          </>
        ) : (
          <button className={page === "SMTPs" ? "active" : ""} onClick={() => setPage("SMTPs")}>SMTPs<span className="nav-caret">⌄</span></button>
        )}

        <div className="sx-nav-profile" onMouseLeave={() => setProfileOpen(false)}>
          <button
            type="button"
            className={profileOpen ? "sx-nav-profile-btn active" : "sx-nav-profile-btn"}
            onClick={() => setProfileOpen((v) => !v)}
            title={currentUser?.email || ""}
          >
            <span className="sx-nav-profile-avatar">
              {currentUser?.avatar ? currentUser.avatar : String(currentUser?.name || currentUser?.email || "U").charAt(0).toUpperCase()}
            </span>
            <span className="sx-nav-profile-meta">
              <span className="sx-nav-profile-name">{currentUser?.name || currentUser?.email || "User"}</span>
              <span className="sx-nav-profile-role">{isAdmin ? "Admin" : "User"}</span>
            </span>
            <span className="sx-nav-profile-caret">⌄</span>
          </button>
          <button
            type="button"
            className="sx-nav-exit"
            aria-label="Log out"
            title="Log out"
            onClick={() => logout()}
          >
            <Icon name="logout" size={18} />
          </button>
          {profileOpen ? (
            <div className="sx-nav-profile-menu">
              <button
                type="button"
                className="sx-nav-profile-head sx-nav-profile-head-btn"
                onClick={() => { setProfileOpen(false); setPage("Profile"); }}
                title="View profile"
              >
                <span className="sx-nav-profile-avatar lg">
                  {currentUser?.avatar ? currentUser.avatar : String(currentUser?.name || currentUser?.email || "U").charAt(0).toUpperCase()}
                </span>
                <div className="sx-nav-profile-headmeta">
                  <div className="sx-nav-profile-hname">{currentUser?.name || "User"}</div>
                  <div className="sx-nav-profile-email">{currentUser?.email || ""}</div>
                  <span className={isAdmin ? "sx-nav-role-badge admin" : "sx-nav-role-badge"}>{isAdmin ? "Admin" : "User"}</span>
                </div>
              </button>
              <div className="sx-nav-profile-sep" />
              <button className="sx-nav-profile-item danger" onClick={() => logout()}>
                <Icon name="logout" size={16} /> Log out
              </button>
            </div>
          ) : null}
        </div>

      </nav>

      <button
        type="button"
        className={mobileMenuOpen ? "sx-mobile-nav-overlay show" : "sx-mobile-nav-overlay"}
        aria-label="Close navigation menu"
        onClick={() => setMobileMenuOpen(false)}
      />

      <GlobalSearchPanel open={searchOpen} onClose={() => setSearchOpen(false)} />

      <header className="sx-topbar">
        <div className="sx-topbar-title">{page}</div>
        <div className="nav-right sx-reference-icons">
          <button
            type="button"
            className={mobileMenuOpen ? "sx-icon-btn sx-mobile-menu-btn open" : "sx-icon-btn sx-mobile-menu-btn"}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((value) => !value)}
          >
            <span></span><span></span><span></span>
          </button>
          <button
            type="button"
            className="sx-icon-btn sx-theme-btn"
            aria-label="Toggle theme"
            onClick={() => setDarkMode((value) => !value)}
            title={darkMode ? "Switch to white and blue mode" : "Switch to current dark mode"}
          >
            {darkMode ? <Icon name="moon" size={19} /> : <Icon name="sun" size={19} />}
          </button>
          <button type="button" className="sx-icon-btn" aria-label="Search contacts" onClick={() => setSearchOpen(true)}>
            <Icon name="search" size={19} />
          </button>
          <button type="button" className="sx-icon-btn" aria-label="Settings">
            <Icon name="settings" size={18} />
          </button>
          <div className="sx-notification-wrap">
            <button
              type="button"
              className="sx-icon-btn"
              aria-label="Notifications"
              onClick={() => setNotificationsOpen((value) => !value)}
            >
              <Icon name="bell" size={18} />
            </button>
            <NotificationsDropdown open={notificationsOpen} />
          </div>
        </div>
      </header>

      {isAdmin && viewAs ? (
        <div className="sx-viewas-banner">
          <span className="sx-viewas-text">
            👁 Viewing data as <strong>{viewAs.name || viewAs.email || "user"}</strong>
            {viewAs.email ? <span className="sx-viewas-email"> ({viewAs.email})</span> : null}
          </span>
          <button
            type="button"
            className="sx-viewas-exit"
            onClick={() => {
              clearViewAs();
              window.location.hash = "#/Users";
              window.location.reload();
            }}
          >Exit user view</button>
        </div>
      ) : null}

      <main className="sx-main">{pages[page] || pages.Dashboard}</main>

      <button className="chat-btn">💬</button>
    </div>
  );
}